import React from 'react';
import {Link} from "react-router";
import { existingFileError, missingFileError, existingFileErrorMessage, fileTypes } from '../../consts/consts';
import { removeFile, uploadFile } from '../../requests/fileRequests';
import { toMediaUrl } from '../../utils';
import Input from "../Input-new/Input";
const FOLDER = '';

/** Legacy Arc storage prefixed every upload with a hex digest: "<hash>_<original name>". */
const ARC_HASH_PREFIX = /^[0-9a-f]{32,}_/i;

/** Photos briefly carried a "_<epoch ms>" suffix; strip it from names stored back then. */
const PHOTO_TIMESTAMP = /_\d{13}(?=\.[^.]*$)|_\d{13}$/;

/** Longest filename shown before the middle is elided. */
const MAX_DISPLAYED_NAME = 28;

/** Bucket-relative path of a stored file, so removals target the right object. */
function storagePathFromLink(link) {
    if (!link) return '';
    const path = String(link).split('?')[0];
    return /^https?:\/\//i.test(path) ? path.replace(/^https?:\/\/[^/]+\//i, '') : path;
}

/**
 * The name the user uploaded, recovered from the stored path: drop the folders,
 * the legacy Arc hash prefix, and any timestamp left on older photos.
 */
function displayNameFromLink(link, fileType) {
    const raw = storagePathFromLink(link).split('/').pop();
    if (!raw) return null;

    let name = raw;
    if (/%[0-9A-Fa-f]{2}/.test(raw)) {
        try {
            name = decodeURIComponent(raw);
        } catch (e) {
            name = raw;
        }
    }

    name = name.replace(ARC_HASH_PREFIX, '');

    return fileType === fileTypes.Photo ? name.replace(PHOTO_TIMESTAMP, '') : name;
}

/** Elide the middle of long names rather than the extension, which users read. */
function truncateName(name) {
    if (!name || name.length <= MAX_DISPLAYED_NAME) return name;

    const lastDot = name.lastIndexOf('.');
    const ext = lastDot > 0 ? name.slice(lastDot) : '';
    const head = name.slice(0, Math.max(4, MAX_DISPLAYED_NAME - ext.length - 1));

    return `${head}…${ext}`;
}

export default class UploadFile extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            file: null,
            base64URL: null,
            error: null,
            redirectLink: null,
            uploading: false,
            uploadedPath: null, // bucket-relative path of the stored file, for remove()
        };

        this.upload = this.upload.bind(this);
        this.remove = this.remove.bind(this);
        this.validateSelectedFile = this.validateSelectedFile.bind(this);
        this.getBase64 = this.getBase64.bind(this);
        this.triggerUpload = this.triggerUpload.bind(this);

        this.fileInputRef = React.createRef();
    }

    triggerUpload() {
        if (this.state.file && this.state.base64URL && this.props.uploadOnSave) {
            return this.upload(this.props.name);
        }
        return Promise.resolve();
    }

    componentDidMount() {
        this.syncStateFromLink(this.props.link);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.link !== this.props.link) {
            this.syncStateFromLink(this.props.link);
        }
    }

    syncStateFromLink(link) {
        if (link) {
            this.setState({
                redirectLink: link,
                file: { name: displayNameFromLink(link, this.props.fileType) },
                uploadedPath: storagePathFromLink(link),
                error: null,
            });
        } else {
            this.setState({
                redirectLink: null,
                file: null,
                uploadedPath: null,
                error: null,
            });
        }
    }

    getBase64(file) {
        return new Promise(resolve => {
            let baseURL = "";
            let reader = new FileReader();

            reader.readAsDataURL(file);
            reader.onload = () => {
                baseURL = reader.result;
                resolve(baseURL);
            };
        });
    }

    upload(key) {
        const fileType = this.props.fileType;
        // Save under the exact filename the user chose (e.g. "Dawson Heide - Headshot.jpg")
        // for both photos and documents. Photos used to get a "_<timestamp>" suffix, which
        // showed up as a string of numbers appended to the name.
        const fileName = this.state.file.name;
        const base64File = this.state.base64URL;
        const folder = this.props.folder || FOLDER;
        this.setState({ uploading: true });
        this.props.onUploadingChange && this.props.onUploadingChange(true);
        return uploadFile({fileName, base64File, folder}).then(res => {
            res = res.data.responses[0];
            if (!res || !res.success) {
                if (res.errorCode === existingFileError) {
                    if (fileType !== fileTypes.Photo) {
                        const fileName = this.state.file.name.split('.')[0];
                        const timestamp = Date.now();
                        const newName = fileName + '_' + this.props.data.ADDRESS + `_${timestamp}` + '.pdf';

                        this.setState({
                            file: {
                                name: newName
                            },
                        }, () => this.upload(key));
                    } else {
                        this.setState({
                            file: null,
                            error: existingFileErrorMessage,
                            uploading: false,
                        });
                        this.props.onUploadingChange && this.props.onUploadingChange(false);
                    }
                } else {
                    this.setState({
                        error: res && res.message ? res.message : "Unspecified error",
                        uploading: false,
                    });
                    this.props.onUploadingChange && this.props.onUploadingChange(false);
                }
            } else {
                const redirectLink = res.data.path;
                this.setState({
                    redirectLink: redirectLink,
                    error: null,
                    uploading: false,
                    uploadedPath: storagePathFromLink(redirectLink),
                    file: { name: displayNameFromLink(redirectLink, fileType) },
                });
                this.props.onUploadingChange && this.props.onUploadingChange(false);
                this.props.handleChange(key, redirectLink);
            }
        }).catch(err => {
            this.setState({
                error: err && err.message ? err.message : "Upload failed",
                uploading: false,
            });
            this.props.onUploadingChange && this.props.onUploadingChange(false);
        });
    }

    remove(key) {
        const fileName = this.state.uploadedPath || this.state.file.name;
        // A stored path already carries its folder (including legacy "arcimedes/user/331/…").
        const folder = fileName.includes('/') ? '' : (this.props.folder || FOLDER);

        removeFile({fileName, folder}).then(res => {
            const success = res && (res.success === true || (res.data && res.data.success === true));
            const errorCode = res && (res.errorCode || (res.data && res.data.errorCode));
            const alreadyGone = errorCode === missingFileError;

            if (success || alreadyGone) {
                this.fileInputRef.current.value = null;
                this.props.handleChange(key, null);
                this.setState({
                    file: null,
                    redirectLink: null,
                    base64URL: null,
                    uploadedPath: null,
                    error: null,
                });
            } else {
                const message = (res && res.message) || (res && res.data && res.data.message);
                this.setState({ error: message || "Unspecified error" });
            }
        }).catch(err => {
            this.setState({
                error: err && err.message ? err.message : "Remove failed",
            });
        });
    }

    validateSelectedFile(event) {
        let selectedFile = event.currentTarget.files[0];
        const fileSize = selectedFile.size / 1000000; //mb

        if (fileSize > 20){
            this.setState({error:'Maximum file size exceeded'});
            return;
        }
        
        this.getBase64(selectedFile).then(result => {
            selectedFile["base64"] = result;
            this.setState({
                error: null,
                file: selectedFile,
                base64URL: result,
                uploadedPath: null,
            }, () => this.upload(this.props.name));
        });
    }

    render() {
        const {data, label, name, disabled, className, deleted, viewMode = false, link, fileType, uploadManually, handleChangeInput, emptyText} = this.props;
        // What to show while no file is attached. Callers can override it per
        // field — the DRAW form reads "N/A". (Claritask 547010)
        const noFileText = emptyText || `No ${fileType} selected`;
        let value =  this.state.file ? this.state.file.name : null;

        if (!this.props.link && !this.state.file) {
            value = null
        }

        return (
            <div className={`file-container ${className}`}>
                {
                    uploadManually ?
                        <Input
                            className="Website_column_item"
                            type="text"
                            valueType="text"
                            name={name}
                            label={label}
                            value={data[name]}
                            disabled={disabled}
                            handleParentChange={handleChangeInput}
                            viewMode={viewMode}
                            deleted={deleted}
                        />
                    :
                        <React.Fragment>
                            {
                                label &&
                                <label className={`file-label ${disabled ? 'disabled' : ''}`}>{label}</label>
                            }
                            <div className='file-input-container'>
                                <div className="file-text-container">
                                    {
                                        this.state.uploading ?
                                            <span className="file-uploading">Uploading...</span>
                                            : !value ?
                                            <span className={disabled ? 'disabled':''}>{noFileText}</span>
                                            :
                                            !viewMode &&
                                            <span className={disabled ? 'disabled':''} title={value}>
                                                {truncateName(value)}
                                            </span>
                                    }
                                    {
                                        viewMode && value &&
                                        <div className={`file-view ${disabled ? 'disabled': ''}${deleted ? ' file-view-mode-deleted' : ''}`}>
                                            <Link target='_blank'
                                                disabled={disabled}
                                                className="green"
                                                title={value}
                                                to={toMediaUrl(link)}>
                                                {
                                                    /* A photo is worth looking at rather than reading the
                                                       filename of. Everything else stays a link. */
                                                    fileType === fileTypes.Photo
                                                        ? <img className="file-view__photo" src={toMediaUrl(link)} alt={value} title={value} />
                                                        : truncateName(value)
                                                }
                                            </Link>
                                        </div>
                                    }
                                    {  this.state.error &&
                                        <span className="error">{this.state.error}</span>
                                    }
                                </div>
                                {
                                    !viewMode &&
                                    <div className='file-wrapper'>
                                        <input className='hidden' type="file" ref={this.fileInputRef} accept={fileType === fileTypes.Photo ? "image/png, image/jpeg, image/jpg, .svg" : ".pdf"} onChange={this.validateSelectedFile} disabled={disabled || this.state.uploading} />
                                        <input className={`button-new ${!value ? 'positive':'negative'} ${disabled || this.state.uploading ? 'inactive' : ''}`}
                                               disabled={disabled || this.state.uploading}
                                               type="button"
                                               value={this.state.uploading ? 'Uploading...' : `${!value ?'Choose' : 'Remove'} ${fileType}`}
                                               onClick={!value ? () => this.fileInputRef.current.click() : () => this.remove(name)} />

                                    </div>
                                }
                            </div>
                        </React.Fragment>
                }
            </div>
        )
    }
}   
